import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { LoaderCircle, X } from "lucide-react";
import { formatDate } from "./reportRow";

/**
 * What Albert holds for one Sensi report, in a dialog over the upload list.
 *
 * The PDF was discarded at upload, so there is no document to render. What
 * survives is the parsed report, laid out the way the page it came from reads:
 * the opening narrative, then each insight with its recommendations, what was
 * already done about it and the moments behind it, then the events worth
 * knowing about.
 *
 * It renders through a portal onto document.body. The panel sits deep inside
 * the client page, where a fixed element is positioned against the nearest
 * transformed ancestor rather than the viewport — so from where it is mounted,
 * no z-index would reliably clear the app's own chrome.
 */

// Above the sticky top nav (100), the sidebar (200, and 300 once it goes fixed)
// and the app's other layers, but under the screen lock at 9999: a report
// preview should never cover a locked screen.
const OVERLAY_Z = 9000;

// Sensi hedges in its own headings. Keeping the hedge visible matters: a
// "Suspected" finding is not a diagnosis, and the panel shouldn't imply one.
const KIND_META = {
  possible: { label: "Possible", tone: "bg-amber-50 text-amber-700 ring-amber-200" },
  suspected: { label: "Suspected", tone: "bg-orange-50 text-orange-700 ring-orange-200" },
  positive: { label: "Positive", tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  other: { label: "Noted", tone: "bg-slate-100 text-slate-600 ring-slate-200" },
};

const Section = ({ title, count, children }) => (
  <section className="space-y-3">
    <h3 className="text-sm font-semibold text-slate-800">
      {title}
      {count ? <span className="ml-2 text-xs font-normal text-slate-400">{count}</span> : null}
    </h3>
    {children}
  </section>
);

const Bullets = ({ label, items, tone }) =>
  items?.length ? (
    <div>
      <p className={`text-xs font-semibold uppercase tracking-wide ${tone}`}>{label}</p>
      <ul className="mt-1 space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 text-sm text-slate-700">
            <span aria-hidden="true" className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-slate-300" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

const Insight = ({ insight }) => {
  const kind = KIND_META[insight.kind] || KIND_META.other;
  return (
    <article className="rounded-xl border border-slate-200 p-3 sm:p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-900">{insight.title}</h4>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${kind.tone}`}
        >
          {kind.label}
        </span>
      </div>
      {insight.rationale ? (
        <p className="mt-1 text-sm text-slate-500">{insight.rationale}</p>
      ) : null}

      <div className="mt-3 space-y-3">
        <Bullets
          label="Recommendations"
          items={insight.recommendations}
          tone="text-slate-400"
        />
        <Bullets
          label="Action taken"
          items={insight.actionsTaken}
          tone="text-emerald-600"
        />
      </div>

      {insight.highlightedMoments?.length ? (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Highlighted moments
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
            {insight.highlightedMoments.map((moment, i) => (
              <li key={i} className="text-xs">
                <span className="font-medium text-slate-700">{moment.category}</span>
                {moment.at ? <span className="text-slate-400"> · {moment.at}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
};

const NotableEvent = ({ event }) => (
  <article className="rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3">
    <h4 className="text-sm font-semibold text-amber-900">{event.type}</h4>
    {event.occurrences?.length ? (
      <ul className="mt-1 space-y-0.5">
        {event.occurrences.map((occurrence, i) => (
          <li key={i} className="text-xs text-slate-600">
            {occurrence.raw}
          </li>
        ))}
      </ul>
    ) : null}
    {event.actionTaken ? (
      <p className="mt-1.5 text-sm text-slate-700">
        <span className="font-medium">Action taken:</span> {event.actionTaken}
      </p>
    ) : null}
    {event.note ? <p className="mt-1 text-sm text-slate-600">{event.note}</p> : null}
  </article>
);

const ParsedReport = ({ report }) => {
  const x = report?.extraction;
  if (!x) {
    return (
      <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
        Albert kept no parsed report for this upload.
      </p>
    );
  }

  const takeaways = x.mainTakeaways?.paragraphs || [];
  const insights = x.insights || [];
  const events = x.notableEvents || [];

  if (!takeaways.length && !insights.length && !events.length) {
    return (
      <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
        This report was read by an earlier version of Albert and its data is no
        longer in a shape the panel can show. Upload the PDF again to re-read it.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {x.client?.fullName || x.reportPeriod?.raw ? (
        <div className="space-y-0.5">
          {x.client?.fullName ? (
            // Named as the report's own header names them, so a file attached to
            // the wrong client is visible here rather than only in the data.
            <p className="text-sm">
              <span className="text-slate-500">Care report for </span>
              <span className="font-semibold text-slate-900">{x.client.fullName}</span>
            </p>
          ) : null}
          {x.reportPeriod?.raw ? (
            <p className="text-sm text-slate-500">
              Covers <span className="font-medium text-slate-800">{x.reportPeriod.raw}</span>
            </p>
          ) : null}
        </div>
      ) : null}

      {takeaways.length ? (
        <Section title="Main takeaways">
          <div className="space-y-2">
            {takeaways.map((paragraph, i) => (
              <p key={i} className="text-sm leading-relaxed text-slate-700">
                {paragraph}
              </p>
            ))}
          </div>
        </Section>
      ) : null}

      {insights.length ? (
        <Section title="Top care recommendations" count={insights.length}>
          <div className="space-y-3">
            {insights.map((insight, i) => (
              <Insight key={i} insight={insight} />
            ))}
          </div>
        </Section>
      ) : null}

      {events.length ? (
        <Section title="Notable events" count={events.length}>
          <div className="space-y-2">
            {events.map((event, i) => (
              <NotableEvent key={i} event={event} />
            ))}
          </div>
        </Section>
      ) : null}

      {x.closing?.message || x.closing?.signedBy ? (
        <footer className="border-t border-slate-100 pt-3">
          {x.closing.message ? (
            <p className="text-sm italic text-slate-500">{x.closing.message}</p>
          ) : null}
          {x.closing.signedBy ? (
            <p className="mt-0.5 text-sm font-medium text-slate-700">
              {x.closing.signedBy}
            </p>
          ) : null}
        </footer>
      ) : null}
    </div>
  );
};

const SensiReportPreview = ({ row, report, loading, error, onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // A column bounded by the viewport, so the body is the only thing that
  // scrolls and the header stays where the reader left it. The old markup let
  // the backdrop scroll too, which carried the title up under the top nav.
  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/40 p-3 sm:p-6"
      style={{ zIndex: OVERLAY_Z }}
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Report preview: ${row?.fileName || ""}`}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl sm:max-h-[calc(100dvh-3rem)]"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5 sm:py-4">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-slate-900">
              {row?.fileName}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Uploaded {formatDate(row?.uploadedAt)}
              {row?.pageCount ? ` · ${row.pageCount} pages` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {loading ? (
            <p className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading report…
            </p>
          ) : error ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          ) : (
            <ParsedReport report={report} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SensiReportPreview;
